<template>
  <v-autocomplete
    label="Opportunity"
    v-model="model"
    @input="handleInput"
    v-on="$listeners"
    :items="opportunities"
    item-text="name"
    item-value="id"
    :value-comparator="valueComparator"
    @update:searchInput="onSearch"
    :loading="isLoading"
    :required="required"
    :rules="rules || []"
    hide-no-data
    no-filter
    clearable
  >
    <template slot="item" slot-scope="{ item: opportunity }">
      <v-list-tile-avatar>
        <img :src="opportunity.logo || '/img/missing/logos/organizations/original.png'" />
      </v-list-tile-avatar>

      <v-list-tile-content v-if="opportunity && opportunity.organization">
        <v-list-tile-title v-if="opportunity.name" v-html="opportunity.name" />
        <v-list-tile-sub-title
          v-if="opportunity.organization.name"
          v-html="opportunity.organization.name"
        />
      </v-list-tile-content>
    </template>

    <template slot="selection" slot-scope="{ item }">
      <span v-if="item && item.name">{{ item.name }}</span>
    </template>
  </v-autocomplete>
</template>

<script>
// Helpers
import { mapState } from 'vuex'
import _ from 'lodash'

// Vue Component
export default {
  created() {
    this.$store.dispatch('admin/opportunities/search', {
      sortBy: 'closes_at',
      descending: false,
      page: 1,
      rowsPerPage: 25,
    })

    // On initial load, if a value (id) is provided and not in store, fetch it once
    const id = this.normalizedValue(this.value)
    if (id && !this.$store.getters['admin/opportunities/find'](id)) {
      this.markFetched(id)
      this.$store.dispatch('admin/opportunities/fetch', id)
    }
  },

  props: {
    value: { required: true },
    required: { type: Boolean, default: () => false },
    rules: { type: Array, default: () => [] },
    filter: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      // Always keep model as an ID (number) to avoid [Object object] flicker
      model: this.normalizedValue(this.value),
      search: '',
      fetchedIds: {}, // simple map of id => true to guard duplicate fetches
    }
  },

  computed: {
    ...mapState('admin/opportunities', ['active', 'loading', 'total', 'page', 'rowsPerPage']),


    selectedId() {
      return this.normalizedValue(this.model)
    },

    find() {
      const id = this.selectedId || this.normalizedValue(this.value)
      if (id) return this.$store.getters['admin/opportunities/find'](id)
      return {}
    },

    isLoading() {
      return this.loading || !!(this.find && this.find.loading)
    },

    opportunities() {
      let list = []
      if (this.active && this.active.length) {
        if (this.filter != {}) {
          list = this.active.filter(opp => {
            const org_filter = _.get(this.filter, 'organization.id')
            if (org_filter && opp.organization.id != org_filter) return false
            return true
          })
        } else {
          list = this.active
        }
      }
      // Only inject the selected item if it is fully loaded (has a name)
      const selected = this.find
      if (selected && selected.id != null && selected.name) {
        const exists = list.some(o => o && o.id == selected.id)
        if (!exists) list = [selected, ...list]
      }
      return list
    },
  },

  watch: {
    // Sync incoming prop to internal ID model
    value(newVal) {
      this.model = this.normalizedValue(newVal)
      const id = this.model
      if (id && !this.$store.getters['admin/opportunities/find'](id) && !this.hasFetched(id)) {
        this.markFetched(id)
        this.$store.dispatch('admin/opportunities/fetch', id)
      }
    },

    // Debounced search; do not trigger fetches for selected id repeatedly
    search: _.debounce(function(name) {
      if (name && name.length > 2) {
        this.$store.dispatch('admin/opportunities/search', {
          search: name,
          sortBy: 'closes_at',
          descending: false,
          page: 1,
          rowsPerPage: 500,
        })
      }
    }, 250),
  },

  methods: {
    normalizedValue(val) {
      if (val == null || val === '') return null
      if (val && typeof val === 'object') return Number(val.id)
      const n = Number(val)
      return isNaN(n) ? null : n
    },
    hasFetched(id) {
      return !!this.fetchedIds[id]
    },
    markFetched(id) {
      this.$set(this.fetchedIds, id, true)
    },
    handleInput(selected) {
      // Ensure we always emit an ID
      const id = this.normalizedValue(selected)
      this.$emit('input', id)
      // Optionally fetch details for newly selected id once
      if (id && !this.$store.getters['admin/opportunities/find'](id) && !this.hasFetched(id)) {
        this.markFetched(id)
        this.$store.dispatch('admin/opportunities/fetch', id)
      }
    },
    onSearch(val) {
      this.search = typeof val === 'string' ? val : ''
    },
    valueComparator(a, b) {
      // a is model value or item-value, b the other side; normalize
      const na = typeof a === 'object' ? Number(a && a.id) : Number(a)
      const nb = typeof b === 'object' ? Number(b && b.id) : Number(b)
      if (isNaN(na) || isNaN(nb)) return a === b
      return na === nb
    },
  },
}
</script>
